import {
	Component,
	Input,
	Output,
	OnInit,
	OnDestroy,
	EventEmitter,
	OnChanges,
	SimpleChanges,
	SimpleChange,
	DoCheck,
	KeyValueDiffers,
	KeyValueDiffer,
	forwardRef
} from '@angular/core';
import {
	FormGroup,
	Validator,
	ControlValueAccessor,
	NG_VALUE_ACCESSOR,
	NG_VALIDATORS
} from '@angular/forms';
import { AutocompleteService, ValueModel, FormHelper } from '../../core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

type Highlighted = 'source' | 'target' | null;

@Component({
	selector: 'sm-multiselect-boxes',
	template: require('./sm-multiselect-boxes.component.html'),
	providers: [
		{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => SmMultiSelectBoxesComponent), multi: true },
		{ provide: NG_VALIDATORS, useExisting: forwardRef(() => SmMultiSelectBoxesComponent), multi: true }
	]
})
export class SmMultiSelectBoxesComponent implements Validator, ControlValueAccessor, OnInit, OnDestroy, OnChanges, DoCheck {
	@Input('datasource') dataSource: string = '';
	@Input('datasourceparams') dataSourceParams: { [key: string]: string } = {};
	@Input() required: boolean = false;

	@Output('change') onChange: EventEmitter<ValueModel[]> = new EventEmitter();
	@Output('loaded') onLoaded: EventEmitter<ValueModel[]> = new EventEmitter();
	@Input() set id(value: string) {
		this._id = value;
		this.sourceZones = ['SmMultiSelectBoxesComponent_source_' + value];
		this.targetZones = ['SmMultiSelectBoxesComponent_target_' + value];
	}

	get id() {
		return this._id;
	}

	loading: boolean = false;
	isDisabled: boolean = false;
	source: ValueModel[] = [];
	target: ValueModel[] = [];
	sourceZones: string[] = [];
	targetZones: string[] = [];
	higlighted: Highlighted = null;

	private _paramsDiffer: KeyValueDiffer<string, string>;
	private _allValues: ValueModel[] = [];
	private _selected: number[] = [];
	private _subs: Subscription[] = [];
	private _id: string;

	constructor(
		private _autocompleteService: AutocompleteService,
		keyValueDiffers: KeyValueDiffers
	) {
		this._paramsDiffer = keyValueDiffers.find({}).create();
	}

	/// LIFECYCLE

	ngOnChanges(changes: SimpleChanges) {
		let dsChange: SimpleChange = changes['datasource'];
		if (dsChange && !dsChange.isFirstChange()) {
			this.load();
		}
	}

	ngDoCheck() {
		if (this.dataSource && this.dataSourceParams) {
			let changes = this._paramsDiffer.diff(this.dataSourceParams);
			if (changes) {
				this.load();
			}
		}
	}

	ngOnInit() {
		if (this.id == null) {
			throw 'ID must be set!';
		}

		this.load();
	}

	ngOnDestroy() {
		this._subs.forEach(s => s && s.unsubscribe());
	}

	/// METHODS

	//#region ControlValueAccessor implementation

	writeValue(obj: any): void {
		this._selected = obj;
		this.compute();
	}

	registerOnChange(fn: any): void {
		this._subs.push(
			this.onChange.subscribe((changes) => {
				fn(this.target.map(t => t.value as number));
			})
		);
	}

	registerOnTouched(fn: any): void { }

	setDisabledState(isDisabled: boolean): void {
		this.isDisabled = isDisabled;
	}

	higlight(type?: Highlighted) {
		this.higlighted = type;
	}

	//#endregion ControlValueAccessor implementation

	//#region Validator

	validate(c: FormGroup) {
		if (this.required && this.target.length < 1) {
			return { required: true };
		}
		return null;
	}

	//#endregion Validator

	addInto(source: ValueModel[], target: ValueModel[], $event: any) {
		let data = $event.dragData;
		const sourceIdx = source.indexOf(data);
		if (sourceIdx > -1) {
			source.splice(sourceIdx, 1);
		}

		target.push(data);

		this.higlight();
		this.fireChangeEvent();
	}

	addAll() {
		this.target = [...this._allValues];
		this.source.length = 0;
		this.fireChangeEvent();
	}

	removeAll() {
		this.source = [...this._allValues];
		this.target.length = 0;
		this.fireChangeEvent();
	}

	/// PRIVATE METHODS

	private fireChangeEvent() {
		this.onChange.emit(this.target);
	}

	private compute() {
		this.source.length = 0;
		this.target.length = 0;

		this._allValues.forEach((v: ValueModel) => {
			let idx = this._selected.findIndex(s => s == (v.value as number));
			if (idx == -1) {
				this.source.push(v);
			}
			else {
				this.target.push(v);
			}
		});
	}

	private load() {
		if (this.dataSource) {
			this.loading = true;

			this._autocompleteService
				.get(this.dataSource, { parameters: this.dataSourceParams })
				.pipe(finalize(() => this.loading = false))
				.subscribe(
					data => {
						this._allValues = data;
						this.compute();
						this.onLoaded.emit(this._allValues);
					}
				);
		}
	}
}


// WEBPACK FOOTER //
// ./node_modules/angular-router-loader/src!./node_modules/angular2-template-loader!./app/shared/components/sm-multiselect-boxes.component.ts